export const MAX_YOUTUBE_VIDEOS = 10;

export function normalizeYouTubeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("http") ? trimmed : `https://${trimmed}`;
}

export function isYouTubeUrl(url: string): boolean {
  const ytRegex = /^(https?:\/\/)?(www\.youtube\.com|youtu\.?be)\/.+$/;
  return ytRegex.test(url);
}

export function getYouTubeEmbedUrl(url: string): string | null {
  const videoId = getYouTubeVideoId(url);
  return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
}

export function getYouTubeWatchUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname === "www.youtube.com" && urlObj.pathname === "/watch") {
      return url;
    }
    if (urlObj.hostname === "youtu.be") {
      const vid = urlObj.pathname.slice(1).split("?")[0];
      return vid ? `https://www.youtube.com/watch?v=${vid}` : url;
    }
  } catch {
    /* ignore invalid URLs */
  }
  return url;
}

function getYouTubeVideoId(url: string): string | null {
  try {
    const urlObj = new URL(normalizeYouTubeUrl(url));
    const host = urlObj.hostname.replace(/^www\./, "");
    if (
      (host === "youtube.com" || host === "m.youtube.com" || host === "music.youtube.com") &&
      urlObj.pathname === "/watch"
    ) {
      return urlObj.searchParams.get("v");
    }
    if (host === "youtu.be") {
      return urlObj.pathname.slice(1).split("/")[0] || null;
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      const embedMatch = urlObj.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?#]+)/);
      if (embedMatch?.[1]) return embedMatch[1];
    }
  } catch {
    /* ignore invalid URLs */
  }
  return null;
}

export function getYoutubeLinksFromSong(song: {
  youtubeLinks?: string[];
  youtubeLink?: string;
  youtubeURL?: string;
}): string[] {
  if (Array.isArray(song.youtubeLinks) && song.youtubeLinks.length > 0) {
    return song.youtubeLinks
      .map(normalizeYouTubeUrl)
      .filter((link) => link && isYouTubeUrl(link))
      .slice(0, MAX_YOUTUBE_VIDEOS);
  }

  const legacy = normalizeYouTubeUrl(song.youtubeLink || song.youtubeURL || "");
  if (legacy && isYouTubeUrl(legacy)) {
    return [legacy];
  }

  return [];
}

export function prepareYoutubeLinksForSave(rawLinks: string[]): {
  links: string[];
  error?: "invalid" | "max";
} {
  const normalized: string[] = [];
  const seenIds = new Set<string>();

  for (const raw of rawLinks) {
    const link = normalizeYouTubeUrl(raw);
    if (!link) continue;

    if (!isYouTubeUrl(link)) {
      return { links: [], error: "invalid" };
    }

    const videoId = getYouTubeVideoId(link);
    if (videoId && seenIds.has(videoId)) continue;
    if (videoId) seenIds.add(videoId);

    normalized.push(link);
    if (normalized.length > MAX_YOUTUBE_VIDEOS) {
      return { links: [], error: "max" };
    }
  }

  return { links: normalized };
}

export function buildYoutubeFirestoreUpdate(links: string[]) {
  return {
    youtubeLinks: links,
    youtubeLink: links[0] || "",
  };
}

export function getVideoCarouselItemsFromSong(song: {
  title?: string;
  artist?: string;
  youtubeLinks?: string[];
  youtubeLink?: string;
  youtubeURL?: string;
}): { youtubeLink: string; title: string; artist: string }[] {
  const links = getYoutubeLinksFromSong(song);
  return links
    .map((link) => {
      const embedUrl = getYouTubeEmbedUrl(link);
      if (!embedUrl) return null;
      return {
        title: song.title || "",
        artist: song.artist || "",
        youtubeLink: embedUrl,
      };
    })
    .filter((item): item is { youtubeLink: string; title: string; artist: string } => item !== null);
}
